import type {
  FlightURLParams,
  FlightURLProps,
  HotelsSearchOutput,
  HotelsURLParams,
  IRegionsData,
} from '../types';

export const fetchHotelsLocation = async (payload: string) => {
  const regionsResponse = await fetch(
    `https://reservas.smilesviagens.com.br/integra/api/regiao/pesquisar/${payload}/null/pt_BR`
  );
  const regionsData: IRegionsData[] = await regionsResponse.json();

  let formattedRegionsList: HotelsSearchOutput[] = [];

  if (regionsData) {
    const type = 'place';
    formattedRegionsList = regionsData.map(
      ({ id, nmRegiao, sbRegiao, dsCoordenada }) => {
        return {
          id,
          name: nmRegiao,
          description: sbRegiao,
          type,
          lat: dsCoordenada?.coordinates?.[0] || undefined,
          lng: dsCoordenada?.coordinates?.[1] || undefined,
        };
      }
    );
  }

  return {
    name: formattedRegionsList[0].name ?? '',
    id: formattedRegionsList[0].id ?? 0,
  };
};

export const convertMilisecondsToDate = (date: string | number) =>
  new Date(Number(date)).toISOString().split('T')[0];

export const getQueriesFromFlightURL = (url: string) => {
  const urlObj = new URL(url);
  const params: { [key: string]: string } = {};

  urlObj.searchParams.forEach((value, key) => {
    try {
      params[key] = JSON.parse(value);
    } catch {
      params[key] = value;
    }
  });

  return params as unknown as FlightURLParams;
};

export const splitAdultsIntoGroups = (adults: number) => {
  let amount = adults;
  const groups: number[] = [];

  while (amount > 0) {
    const groupSize = Math.min(4, amount);
    groups.push(groupSize);
    amount -= groupSize;
  }

  return groups;
};

export const groupChildrenWithAdults = (
  adultGroups: number[],
  childrenAmount: number
) => {
  const groupsWithChildren: string[] = [];
  let remainingChildren = childrenAmount;

  adultGroups.forEach((adults) => {
    const childrenInGroup = Math.min(3, remainingChildren);
    const childrenArray = Array(childrenInGroup).fill('1');
    groupsWithChildren.push(
      childrenArray.length > 0
        ? `${adults}-${childrenArray.join(',')}`
        : `${adults}`
    );
    remainingChildren -= childrenInGroup;
  });

  return groupsWithChildren.join(':');
};

export const generateHotelsCrossSellURL = async (
  flightUrl: string,
  flightUrlProps: FlightURLProps
) => {
  const params = getQueriesFromFlightURL(flightUrl);

  const flightProps = {
    origin: {
      city: flightUrlProps.origins.firstLeg?.city,
      code: flightUrlProps.origins.firstLeg?.code,
      country: flightUrlProps.origins.firstLeg?.country,
    },
    destination: {
      city: flightUrlProps.destinations.firstLeg?.city,
      code: flightUrlProps.destinations.firstLeg?.code,
      country: flightUrlProps.destinations.firstLeg?.country,
    },
  };

  if (flightUrlProps.isMultiCityFlight) {
    flightProps.origin.city = flightUrlProps.origins.secondLeg?.city;
    flightProps.origin.code = flightUrlProps.origins.secondLeg?.code;
    flightProps.origin.country = flightUrlProps.origins.secondLeg?.country;
    flightProps.destination.city = flightUrlProps.destinations.secondLeg?.city;
    flightProps.destination.code = flightUrlProps.destinations.secondLeg?.code;
    flightProps.destination.country =
      flightUrlProps.destinations.secondLeg?.country;
  }

  // Location
  const hotelLocation = await fetchHotelsLocation(
    flightProps.destination.city ?? ''
  );

  // Dates
  const { departureDate, departureDate2, returnDate } = params;
  const departureDateToConvert = departureDate2 ?? departureDate;
  const checkinDate = departureDateToConvert
    ? convertMilisecondsToDate(departureDateToConvert)
    : '';
  const checkinDateNextDay = convertMilisecondsToDate(
    new Date(checkinDate).setDate(new Date(checkinDate).getDate() + 1)
  );
  let checkoutDate =
    typeof returnDate === 'number'
      ? convertMilisecondsToDate(returnDate)
      : checkinDateNextDay;

  if (checkoutDate === checkinDate) {
    checkoutDate = checkinDateNextDay;
  }

  // Rooms
  const childrenSum = params.children + params.infants;
  const adultsGroup = splitAdultsIntoGroups(params.adults);
  const roomsPayload = groupChildrenWithAdults(adultsGroup, childrenSum);

  const nacionalOrInternationalText = String(
    flightProps.destination.country
  ).includes('Brasil')
    ? 'Nacional'
    : 'Internacional';

  const datalayerDestination = `${nacionalOrInternationalText}: ${flightProps.origin.city} (${flightProps.origin.code}) - ${flightProps.destination.city} (${flightProps.destination.code})`;

  const output: HotelsURLParams = {
    checkin: checkinDate,
    checkout: checkoutDate,
    crossSell: String(true),
    crossSellOrigin: 'AIRLINE_HOME',
    destination: String(hotelLocation.id),
    name: hotelLocation.name,
    rooms: roomsPayload,
    datalayerDestination,
  };

  return new URLSearchParams(output).toString();
};
